import { designTokens } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useEffect, useId, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import useSWRMutation from "swr/mutation";
import type { LoginStaffModel } from "~/components/common/user_login_modal/UserLoginModal";
import type { NskResponse } from "~/models/Api";
import { isSuccess } from "~/utils/api/api_handler";

type Props = {
	loginUser: LoginStaffModel;
	mutateSession: () => Promise<void>;
	onBack: () => void;
	onLogin: (id: number, password: string | null) => Promise<NskResponse>;
};

export function UserLoginModalPassword({
	loginUser,
	onBack,
	onLogin,
	mutateSession
}: Props) {
	const { notify } = useNotification();
	const formId = useId();
	const [showPassword, setShowPassword] = useState(false);
	const passwordRef = useRef<HTMLInputElement | null>(null);

	const { handleSubmit, register } = useForm<{ password: string }>({
		defaultValues: { password: "" }
	});

	const { ref: hookFormRef, ...registerPassword } = register("password");

	useEffect(() => {
		passwordRef.current?.focus();
	}, []);

	const { trigger, isMutating } = useSWRMutation(
		["staff-login", loginUser.id],
		async (_key, { arg }: { arg: string | null }) => onLogin(loginUser.id, arg)
	);

	const onSubmitLogin = handleSubmit(async ({ password }) => {
		const result = await trigger(password);
		if (isSuccess(result)) {
			await mutateSession();
			notify({ content: "ログインしました。", variant: "success" });
		} else {
			notify({
				content: `エラーが発生しました。${result.message ?? ""}`,
				variant: "error"
			});
		}
	});

	return (
		<>
			<ModalBody>
				<form id={formId} noValidate onSubmit={onSubmitLogin}>
					<FormGroup mb={designTokens["spacing/s"]} variant="vertical">
						<FormControl label="パスワード">
							<Input
								type={showPassword ? "text" : "password"}
								{...registerPassword}
								ref={(el) => {
									hookFormRef(el);
									passwordRef.current = el;
								}}
							/>
						</FormControl>
					</FormGroup>
					<InputCheckBox
						checked={showPassword}
						label="パスワードを表示する"
						onChange={(e) => {
							setShowPassword(e.target.checked);
						}}
					/>
				</form>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button
						disabled={isMutating}
						onClick={onBack}
						type="button"
						variant="secondary"
					>
						もどる
					</Button>
				}
				rightArea={
					<Button
						form={formId}
						loading={isMutating}
						type="submit"
						variant="primary"
					>
						ログイン
					</Button>
				}
			/>
		</>
	);
}
